import { useSearchParams } from "react-router-dom";
import { useCustomerSearch } from "./api";

// ============================================================================
// Prev/Next customer cycling — entry-context-aware.
//
// When the dispatcher arrives at Customer 360 from a list (search results
// for now; recents + board-filtered-by-tech + Today attention items in
// future), the URL carries a `from=<kind>` param identifying which list
// they came from. This hook reconstructs the relevant list, finds the
// current customer's position in it, and returns prev/next targets +
// position info that the CustomerNavigation component renders.
//
// Gmail / Linear / GitHub-PR-review pattern: cycling preserves entry
// context. You cycle through your search results when you arrived via
// search, you cycle through your inbox view when you arrived via inbox.
//
// v1 SCOPE (2026-05-26): only `from=search` produces navigation.
// `from=recents` is accepted as an entry tag for forward compatibility,
// but recents cycling is deliberately deferred — viewing a customer
// shoves them to the top of recents, which makes "what's the next item"
// semantically murky. Solved later via either a navigation snapshot
// taken at click-time or an explicit "recents minus current" cycle list.
//
// Direct URL hits (no `from` param) and CustomerLink entries from the
// Board / Today / dispatch modals (also no `from` param) get no
// navigation — by design, those entry points have no natural list.
// ============================================================================

export interface NavTarget {
  custno: string;
  fullname: string | null;
}

export interface CustomerNavigationState {
  prev: NavTarget | null;
  next: NavTarget | null;
  position: { current: number; total: number } | null;
  /**
   * Query string (no leading `?`) that should be preserved when navigating
   * to prev/next so the cycling context follows the user.
   */
  preservedQueryString: string;
  /** True when the underlying list is still loading. */
  isLoading: boolean;
}

const blank: CustomerNavigationState = {
  prev: null,
  next: null,
  position: null,
  preservedQueryString: "",
  isLoading: false,
};

export function useCustomerNavigation(
  currentCustno: string | undefined,
): CustomerNavigationState {
  const [searchParams] = useSearchParams();
  const from = searchParams.get("from");
  const q = searchParams.get("q") ?? "";

  // Hooks must run unconditionally — pass an empty string when we're not
  // in search context so the query stays disabled / cached-empty.
  const isSearchContext = from === "search";
  const { data: searchResults, isLoading } = useCustomerSearch(
    isSearchContext ? q : "",
  );

  if (!currentCustno) return blank;
  if (!isSearchContext) return blank;
  if (isLoading) return { ...blank, isLoading: true };
  if (!searchResults || searchResults.length === 0) return blank;

  const idx = searchResults.findIndex((r) => r.custno === currentCustno);
  if (idx === -1) return blank;

  const preserved = new URLSearchParams();
  preserved.set("from", "search");
  if (q) preserved.set("q", q);

  return {
    prev:
      idx > 0
        ? {
            custno: searchResults[idx - 1].custno,
            fullname: searchResults[idx - 1].fullname,
          }
        : null,
    next:
      idx < searchResults.length - 1
        ? {
            custno: searchResults[idx + 1].custno,
            fullname: searchResults[idx + 1].fullname,
          }
        : null,
    position: { current: idx + 1, total: searchResults.length },
    preservedQueryString: preserved.toString(),
    isLoading: false,
  };
}
